import { CircularProgress } from "@mui/material";
import { ArrowBackIos } from "@mui/icons-material";
import { DurableHitIdentifier, HitStatus, LatestHitComment, QuickSearch, SearchVersion, User } from "aderant-conflicts-models";
import { AddIcon, Flyout } from "@aderant/aderant-react-components";
import { HitInformationTileProps } from "components/Tile/HitInformationTile";
import { Messages } from "components/Tile/Tiles.Messages";
import { RootState } from "MyTypes";
import { HitResult } from "pages/ResultsPage/HitResult";
import React, { CSSProperties, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { getIsFetchingFlyoutEntityDetail, getCurrentUser, getCurrentHitCommentCount } from "state/selectors";
import { conflictsPalette } from "styles/conflictsPalette";
import { HitInformationTile, SecondaryInformationTiles } from "../Tile";
import { UserChangePermissions } from "pages/ResultsPage/ResultsPage";
import { RequestTermsDialog } from "pages/ResultsPage/RequestTermsDialog";
import { CommentsContainer } from "components/HitComments/CommentsContainer";
import { HitDetailFooter } from "components/Footer/HitDetailFooter";
import { processHitResults } from "pages/ResultsPage/HitCardsPage";
import CommentCard from "components/Cards/CommentCard";
import { addCommentStyles } from "pages/ResultsPage/HitDetailMobilePage";
import { FlyoutHeader } from "./FlyoutHeader";

// Constants for consistent sizing and spacing
const FLYOUT_WIDTH = "400px";
const STANDARD_PADDING = "15px";
const CONTENT_GAP = "12px";
const FOOTER_HEIGHT = "99px";
const FOOTER_PADDING = "14px 24px";
const BACK_BUTTON_FONT_SIZE = "14px";
const STATUS_MESSAGE_FONT_SIZE = "1rem";
const CIRCULAR_PROGRESS_SIZE = "2rem";

const flyoutStyle: CSSProperties = {
    backgroundColor: conflictsPalette.background.white,
    width: FLYOUT_WIDTH,
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden"
};

export const flyoutFooter: CSSProperties = {
    backgroundColor: conflictsPalette.background.white,
    boxShadow: "0 2px 4px rgb(0 0 0 / 15%)",
    padding: FOOTER_PADDING,
    width: "100%",
    height: FOOTER_HEIGHT,
    position: "sticky",
    bottom: 0,
    borderTop: `1px solid ${conflictsPalette.border}`
};

const commentsContainer: CSSProperties = {
    backgroundColor: conflictsPalette.background.hoverLight,
    padding: STANDARD_PADDING,
    flex: "1 1 auto",
    overflowY: "auto",
    scrollbarGutter: "stable"
};

const backButtonContainer: CSSProperties = {
    marginBottom: STANDARD_PADDING
};

const backButton: CSSProperties = {
    background: "none",
    border: "none",
    color: conflictsPalette.primary.R2D2,
    cursor: "pointer",
    fontSize: BACK_BUTTON_FONT_SIZE,
    display: "flex",
    fontFamily: "Open Sans",
    alignItems: "center",
    gap: "5px"
};

const backButtonIcon: CSSProperties = {
    fontSize: BACK_BUTTON_FONT_SIZE
};


const overviewContentContainer: CSSProperties = {
    padding: STANDARD_PADDING,
    flex: "1 1 auto",
    overflowY: "auto",
    scrollbarGutter: "stable",
    display: "flex",
    flexDirection: "column",
    gap: CONTENT_GAP
};

const loadingContainer: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};

const statusMessageContainer: CSSProperties = {
    fontSize: STATUS_MESSAGE_FONT_SIZE,
    textAlign: "center"
};

const clickableCommentContainer: CSSProperties = {
    cursor: "pointer"
};

export type HitDetailFlyoutProps = {
    open: boolean;
    onClickAway: (e: MouseEvent | TouchEvent) => void;
    toggleRequestTermsDialog: () => void;
    requestTermsDialogOpen: boolean;
    onClose: () => void;
    data?: HitResult;
    hitComments?: Record<string, LatestHitComment>;
    allUsers: User[];
    entityId?: string;
    userChangePermissions: UserChangePermissions;
    hasReviewerRole?: boolean;
} & HitDetailFlyoutPropsBySearchType;

type HitDetailFlyoutPropsBySearchType =
    | {
          searchVersion?: QuickSearch;
          onHitChange?: never;
          searchReadOnly?: true;
      }
    | {
          searchVersion?: SearchVersion;
          onHitChange?: (status?: HitStatus, hitOwnerId?: string | null) => void;
          searchReadOnly?: boolean;
      };

const getDurableHitIdentifier = (data: HitResult | undefined): DurableHitIdentifier | undefined => {
    return data
        ? {
              requestTermId: data.hitId.requestTermId ?? "",
              hitEntityId: data.hit.sourceData.id ?? "",
              hitEntityType: data.hit.sourceType ?? ""
          }
        : undefined;
};

// Extracted comment section component for better maintainability
interface CommentSectionProps {
    currentUser: any;
    data: HitResult;
    searchVersion?: SearchVersion | QuickSearch;
    onBackToOverview: () => void;
}

const CommentSection = ({ currentUser, data, searchVersion, onBackToOverview }: CommentSectionProps): JSX.Element => {
    return (
        <div style={commentsContainer}>
            <div style={backButtonContainer}>
                <button onClick={onBackToOverview} style={backButton}>
                    <ArrowBackIos style={backButtonIcon} />
                    Back
                </button>
            </div>
            {currentUser && data && (
                <CommentsContainer
                    currentUser={currentUser}
                    durableHitIdentifier={{ 
                        hitEntityId: data.hit.sourceData.id ?? "", 
                        hitEntityType: data.hit.sourceType ?? "", 
                        requestTermId: data.hitId.requestTermId ?? "" 
                    }}
                    searchVersionIdentifier={{ 
                        searchId: searchVersion?.searchId ?? "", 
                        versionId: searchVersion?.id ?? "" 
                    }}
                />
            )}
        </div>
    );
};

const HitDetailFlyout = (props: HitDetailFlyoutProps): JSX.Element => {
    const { open, onClose, data, onClickAway, toggleRequestTermsDialog, requestTermsDialogOpen, entityId, searchVersion, hitComments, allUsers } = props;

    const currentUser = useSelector(getCurrentUser);

    const [showComments, setShowComments] = useState(false);

    // Memoize expensive computations
    const durableHitIdentifier = useMemo(() => getDurableHitIdentifier(data), [data]);
    const commentCount: number = useSelector((state: RootState) => getCurrentHitCommentCount(state, durableHitIdentifier));

    // name is always a string, but HitResult interface has key with multiple types including Date, which can't be assigned to type 'ReactNode'
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const hitResultName = data?.name as string;
    const secondaryTileData = useSelector((state: RootState) => {
        return state.search.hitFlyoutDetail;
    });
    const isFetchingEntity = useSelector((rootState: RootState) => (entityId ? getIsFetchingFlyoutEntityDetail(rootState, entityId) : false));
    
    // Memoize hit information tile props
    const hitInformationTileProps: Partial<HitInformationTileProps> = useMemo(() => {
        return searchVersion?.isQuickSearch
            ? {
                  searchVersion: searchVersion,
                  searchReadOnly: true
              }
            : {
                  searchVersion: searchVersion,
                  searchReadOnly: props.searchReadOnly,
                  onHitChange: props.onHitChange
              };
    }, [searchVersion, props.searchReadOnly, props.onHitChange]);

    // Memoize event handlers
    const handleCommentClick = useCallback((): void => {
        setShowComments(true);
    }, []);

    const handleBackToOverview = useCallback((): void => {
        setShowComments(false);
    }, []);

    const latestCommentData = useMemo(() => {
        if (!data) {
            return undefined;
        }
        const processed = processHitResults([data], hitComments || {}, allUsers);
        return processed.length === 1 ? processed[0] : undefined;
    }, [data, hitComments, allUsers]);

    // Memoize the overview content to prevent unnecessary re-renders
    const overviewContent = useMemo((): JSX.Element => {
        if (data?.isRedacted) {
            return <div></div>;
        }
        
        if (isFetchingEntity) {
            return (
                <div style={loadingContainer}>
                    <CircularProgress variant="indeterminate" color="primary" size={CIRCULAR_PROGRESS_SIZE} title={Messages.LOADING_DATA_PLEASE_WAIT_TITLE.getMessage()} />
                </div>
            );
        }
        
        if (secondaryTileData.entity && secondaryTileData?.entity?.fields?.length > 0) {
            return <SecondaryInformationTiles entityDetails={secondaryTileData.entity} toggleRequestTermsDialog={toggleRequestTermsDialog} />;
        }
        
        return (
            <div role="status" style={statusMessageContainer}>
                {Messages.NO_DATA_AVAILABLE_MESSAGE.getMessage()}
            </div>
        );
    }, [data?.isRedacted, isFetchingEntity, secondaryTileData.entity, toggleRequestTermsDialog]);

    return (
        <div>
            <Flyout open={open} onClose={onClose} closeButton onClickAway={onClickAway}>
                <div style={{ ...flyoutStyle, backgroundColor: conflictsPalette.background.hoverLight }} role="complementary" aria-label="Hit Flyout">
                    <FlyoutHeader 
                        hitResultName={hitResultName}
                        data={data}
                        commentCount={commentCount}
                        showComments={showComments}
                    />

                    {!showComments && (
                        <div style={{ ...overviewContentContainer }}>
                            {latestCommentData?.hitCommentCount ? (
                                <div className="clickable-comment-wrapper" style={clickableCommentContainer} onClick={handleCommentClick}>
                                    <CommentCard
                                        name={latestCommentData?.commentedBy}
                                        time={latestCommentData?.latestCommentedOn}
                                        message={latestCommentData?.latestCommentText}
                                        count={latestCommentData?.hitCommentCount}
                                        isCurrentUser={latestCommentData?.commentedByUserId === currentUser?.id}
                                    />
                                </div>
                            ) : (
                                <div className="add-comment-button" style={addCommentStyles.addCommentContainer} onClick={handleCommentClick}>
                                    <span style={addCommentStyles.addCommentText}>Add a comment</span>
                                    <AddIcon style={addCommentStyles.addCommentIcon} />
                                </div>
                            )}
                            <HitInformationTile data={props.data} userChangePermissions={props.userChangePermissions} {...hitInformationTileProps} />
                            {overviewContent}
                        </div>
                    )}
                    
                    {showComments && currentUser && data && (
                        <CommentSection 
                            currentUser={currentUser}
                            data={data}
                            searchVersion={searchVersion}
                            onBackToOverview={handleBackToOverview}
                        />
                    )}

                    <div style={{ ...flyoutFooter }}>
                        <HitDetailFooter
                            data={data}
                            searchVersion={searchVersion}
                            searchReadOnly={props.searchReadOnly}
                            userChangePermissions={props.userChangePermissions}
                            onHitChange={props.onHitChange}
                            hasReviewerRole={props.hasReviewerRole}
                        />
                    </div>
                </div>
            </Flyout>
            <RequestTermsDialog open={requestTermsDialogOpen} requestTerms={secondaryTileData.entity?.searchRequestTerms ?? []} onClose={toggleRequestTermsDialog} />
        </div>
    );
};

export default HitDetailFlyout;
